import React, { useCallback } from "react";
import { useAppDispatch } from "hooks/redux";
import Portal from "elements/Portal";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { TWebsiteBuilderSectionEntity } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { isWebsiteBuilderStretchElementData } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { isWebsiteBuilderStretchContainerData } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { WEBSITE_BUILDER_PANEL_ID } from "pages/secure/facility/websiteBuilder/editor/shell/panel/WebsiteBuilderPanel";
import { SectionEntityViewStretchSettings } from "./SectionEntityViewStretchSettings";

export interface IUseSectionEntityViewStretchSettingsArgs {
  node: TWebsiteBuilderSectionEntity;
  selected: boolean;
  settingsActive: boolean;
  parentIsSection: boolean;
}

interface IUseSectionEntityViewStretchSettings {
  viewStretchSettings: JSX.Element;
  handleViewStretchSettingsClick: () => void;
}

export function useSectionEntityViewStretchSettings(args: IUseSectionEntityViewStretchSettingsArgs): IUseSectionEntityViewStretchSettings {
  const { node, selected, settingsActive, parentIsSection } = args;
  
  const dispatch = useAppDispatch();

  function getStretch() {
    if (!parentIsSection) {
      return null;
    }
    
    if (node?.type === "element" && isWebsiteBuilderStretchElementData(node.element)) {
      return node.element.stretch;
    } else if (node?.type === "container" && isWebsiteBuilderStretchContainerData(node.container)) {
      return node.container.stretch;
    } else {
      return null;
    }
  }

  const stretch = getStretch();

  const closeSettings = useCallback(() => {
    if (node == null) {
      return;
    }

    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: node.type === "element" ? "element" : "container",
          editingMode: { type: "none" },
        },
      }),
    );
  }, [node]);

  const handleViewStretchSettingsClick = useCallback(() => {
    if (!selected || stretch == null) {
      return;
    }

    if (node.type === "element") {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "element",
            editingMode: { type: settingsActive ? "none" : "element-stretch-settings" },
          },
        }),
      );
    } else if (node.type === "container") {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "container",
            editingMode: { type: settingsActive ? "none" : "container-stretch-settings" },
          },
        }),
      );
    }
  }, [selected, stretch, node, settingsActive]);

  function renderViewStretchSettings() {
    if (!selected || !settingsActive || stretch == null) {
      return null;
    }

    return (
      <Portal parentId={WEBSITE_BUILDER_PANEL_ID}>
        <SectionEntityViewStretchSettings key={node.id} node={node} stretch={stretch} closeSettings={closeSettings} />
      </Portal>
    );
  }

  return {
    viewStretchSettings: renderViewStretchSettings(),
    handleViewStretchSettingsClick,
  };
}
